import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Lock, Star, ArrowRight, RefreshCw, Crown } from 'lucide-react';
import { toast } from 'sonner';
import { DoodleSquiggle, DoodleStar } from './Doodles';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_COLORS = ['#D0D0D0', '#FFC8DD', '#F0C040', '#A2D2FF', '#CDB4DB'];
const STAGE_LABELS = ['Egg', 'Baby', 'Teen', 'Adult', 'Legendary'];

export default function PetGallery({ onSwitch }) {
  const [galleryData, setGalleryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [selectedAnimal, setSelectedAnimal] = useState(null);

  const fetchGallery = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/gallery`, { withCredentials: true });
      setGalleryData(data);
      setSelectedAnimal(data.current_pet_type);
    } catch (e) {
      console.error('Failed to fetch gallery:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchGallery(); }, [fetchGallery]);

  const handleSwitch = async (petType) => {
    if (petType === galleryData.current_pet_type) return;
    setSwitching(true);
    try {
      const { data } = await axios.post(`${API}/pets/switch/${petType}`, {}, { withCredentials: true });
      toast.success(data.message);
      fetchGallery();
      if (onSwitch) onSwitch();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to switch');
    } finally {
      setSwitching(false);
    }
  };

  if (loading) return <div className="neo-card p-8 text-center animate-pulse-soft">Loading gallery...</div>;
  if (!galleryData) return null;

  const gallery = galleryData.gallery;

  return (
    <div className="space-y-6" data-testid="pet-gallery">
      {/* Animal selector */}
      <div className="flex gap-3 justify-center flex-wrap">
        {Object.entries(gallery).map(([key, animal]) => {
          const teenStage = animal.stages[2];
          return (
            <button
              key={key}
              onClick={() => setSelectedAnimal(key)}
              className={`neo-card p-3 flex flex-col items-center gap-1 transition-all ${
                selectedAnimal === key ? 'ring-3 ring-black scale-105' : ''
              } ${animal.is_current ? 'bg-[#FDF0D5]' : 'bg-white'}`}
              data-testid={`gallery-animal-${key}`}
            >
              <div className="w-14 h-14 rounded-full border-2 border-black overflow-hidden">
                {teenStage.image ? (
                  <img src={teenStage.image} alt={animal.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gray-200 flex items-center justify-center">?</div>
                )}
              </div>
              <span className="text-xs font-bold font-['Fredoka']">{animal.name}</span>
              {animal.is_current && (
                <span className="text-[10px] bg-[#B9FBC0] px-2 py-0.5 rounded-full border border-black font-bold">Current</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Evolution stages for selected animal */}
      {selectedAnimal && gallery[selectedAnimal] && (
        <div className="neo-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold font-['Fredoka']">
              {gallery[selectedAnimal].name} Evolution
            </h3>
            {!gallery[selectedAnimal].is_current && (
              <button
                onClick={() => handleSwitch(selectedAnimal)}
                disabled={switching}
                className="neo-btn px-4 py-2 bg-[#A2D2FF] flex items-center gap-2"
                data-testid={`switch-to-${selectedAnimal}`}
              >
                <RefreshCw className={`w-4 h-4 ${switching ? 'animate-spin' : ''}`} />
                <span>{switching ? 'Switching...' : 'Switch Pet'}</span>
              </button>
            )}
          </div>

          {/* Evolution Path */}
          <div className="grid grid-cols-5 gap-3">
            {gallery[selectedAnimal].stages.map((stage, i) => (
              <div key={i} className="text-center relative">
                {/* Connection line */}
                {i < 4 && (
                  <div className="absolute top-10 -right-2 w-4 flex items-center z-0">
                    <ArrowRight className="w-4 h-4 text-gray-300" />
                  </div>
                )}

                <div
                  className={`w-16 h-16 md:w-20 md:h-20 mx-auto rounded-full border-3 overflow-hidden relative mb-2 ${
                    stage.unlocked ? 'border-black' : 'border-gray-300'
                  }`}
                  style={{
                    backgroundColor: stage.unlocked ? STAGE_COLORS[i] : '#f0f0f0',
                    boxShadow: stage.unlocked ? `3px 3px 0px 0px rgba(0,0,0,1)` : 'none',
                  }}
                >
                  {stage.unlocked ? (
                    stage.image ? (
                      <img src={stage.image} alt={stage.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-2xl">🥚</div>
                    )
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-200">
                      <Lock className="w-6 h-6 text-gray-400" />
                    </div>
                  )}

                  {/* Crown for legendary */}
                  {i === 4 && stage.unlocked && (
                    <Crown className="absolute -top-1 left-1/2 -translate-x-1/2 w-4 h-4 text-yellow-500" fill="currentColor" />
                  )}
                </div>

                <p className="font-bold font-['Fredoka'] text-xs">{STAGE_LABELS[i]}</p>
                <p className="text-[10px] text-gray-500 truncate">{stage.unlocked ? stage.title : `Lvl ${stage.min_level}`}</p>

                {/* Stage dot */}
                <div
                  className={`w-3 h-3 mx-auto mt-1 rounded-full border ${
                    stage.unlocked ? 'border-black' : 'border-gray-300'
                  }`}
                  style={{ backgroundColor: stage.unlocked ? STAGE_COLORS[i] : '#f0f0f0' }}
                />
              </div>
            ))}
          </div>

          {/* Switch warning */}
          {!gallery[selectedAnimal].is_current && (
            <div className="mt-4 p-3 bg-[#FDF0D5] rounded-xl border-2 border-black text-sm">
              <p className="font-semibold">Switching pet keeps your level but resets evolution stage. You'll start from Egg again!</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
